import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import BrandLogo from "@/components/common/BrandLogo";
import { useI18n, useT } from "@/context/I18nContext";

/** Public, unauthenticated layout used for all /privacy /terms /cookies /delete-account /ai-safety /privacy-choices
 *  pages. Rendered OUTSIDE ProtectedRoute so visitors (and reviewers) can reach every page without login. */
export default function PublicLayout() {
  const t = useT();
  const { lang, setLang } = useI18n();
  const loc = useLocation();

  const navItems = [
    { to: "/privacy", label: t("public.nav.privacy") },
    { to: "/terms", label: t("public.nav.terms") },
    { to: "/cookies", label: t("public.nav.cookies") },
    { to: "/delete-account", label: t("public.nav.deleteAccount") },
    { to: "/ai-safety", label: t("public.nav.aiSafety") },
    { to: "/privacy-choices", label: t("public.nav.privacyChoices") },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-cream-50 text-ink-900" data-testid="public-layout">
      {/* Public header */}
      <header className="border-b border-border bg-white/70 backdrop-blur" data-testid="public-header">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="public-brand-link">
            <BrandLogo size="sm" />
            <div className="hidden sm:block">
              <div className="font-heading text-base leading-tight">Inner Compass</div>
              <div className="text-xs text-ink-500 leading-tight">{t("brand.subtitle")}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 text-sm" data-testid="public-nav" aria-label="Legal navigation">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`public-nav-${item.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-full transition ${
                    isActive ? "bg-sage-500 text-white" : "text-ink-700 hover:text-ink-900 hover:bg-cream-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-full border border-border p-0.5" data-testid="public-lang-switch">
              <button
                type="button"
                onClick={() => setLang("nl")}
                data-testid="public-lang-nl"
                className={`px-2.5 py-0.5 rounded-full text-xs ${lang === "nl" ? "bg-sage-500 text-white" : "text-ink-700"}`}
              >
                NL
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                data-testid="public-lang-en"
                className={`px-2.5 py-0.5 rounded-full text-xs ${lang === "en" ? "bg-sage-500 text-white" : "text-ink-700"}`}
              >
                EN
              </button>
            </div>
            <Link
              to="/login"
              data-testid="public-signin-link"
              className="hidden sm:inline-flex text-sm rounded-full bg-ink-900 text-white px-4 py-1.5 hover:bg-ink-700"
            >
              {t("auth.signIn")}
            </Link>
          </div>
        </div>

        {/* Mobile / tablet nav */}
        <nav className="lg:hidden overflow-x-auto flex items-center gap-1 px-4 pb-3 text-xs" data-testid="public-nav-mobile" aria-label="Legal navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex-shrink-0 px-3 py-1 rounded-full ${
                  isActive ? "bg-sage-500 text-white" : "text-ink-700 bg-cream-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1" data-testid="public-main" key={loc.pathname}>
        <Outlet />
      </main>

      <PublicFooter />
    </div>
  );
}

export function PublicFooter() {
  const t = useT();
  const year = new Date().getFullYear();
  const companyLines = t("public.footer.companyLines") || [];
  return (
    <footer className="border-t border-border bg-white mt-16" data-testid="public-footer">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-1">
          <div className="flex items-center gap-3">
            <BrandLogo size="sm" />
            <div className="font-heading text-base">Inner Compass</div>
          </div>
          <p className="mt-3 text-sm text-ink-500">{t("brand.tagline")}</p>
        </div>

        <div data-testid="footer-col-legal">
          <div className="text-xs uppercase tracking-wider text-ink-500 mb-3">{t("public.footer.legal")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link data-testid="footer-privacy" to="/privacy" className="hover:text-sage-600">{t("public.nav.privacy")}</Link></li>
            <li><Link data-testid="footer-terms" to="/terms" className="hover:text-sage-600">{t("public.nav.terms")}</Link></li>
            <li><Link data-testid="footer-cookies" to="/cookies" className="hover:text-sage-600">{t("public.nav.cookies")}</Link></li>
            <li><Link data-testid="footer-delete" to="/delete-account" className="hover:text-sage-600">{t("public.nav.deleteAccount")}</Link></li>
            <li><Link data-testid="footer-ai-safety" to="/ai-safety" className="hover:text-sage-600">{t("public.nav.aiSafety")}</Link></li>
            <li><Link data-testid="footer-privacy-choices" to="/privacy-choices" className="hover:text-sage-600">{t("public.nav.privacyChoices")}</Link></li>
            <li><Link data-testid="footer-download" to="/download" className="hover:text-sage-600">{t("download.android")}</Link></li>
          </ul>
        </div>

        <div data-testid="footer-col-company">
          <div className="text-xs uppercase tracking-wider text-ink-500 mb-3">{t("public.footer.company")}</div>
          <ul className="space-y-1 text-sm text-ink-700">
            {Array.isArray(companyLines) && companyLines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>

        <div data-testid="footer-col-contact">
          <div className="text-xs uppercase tracking-wider text-ink-500 mb-3">{t("public.footer.contact")}</div>
          <ul className="space-y-2 text-sm">
            <li><a href="mailto:contact@innercompass.tech" data-testid="footer-mail-contact" className="hover:text-sage-600">contact@innercompass.tech</a></li>
            <li><a href="mailto:support@innercompass.tech" data-testid="footer-mail-support" className="hover:text-sage-600">support@innercompass.tech</a></li>
            <li><a href="mailto:privacy@innercompass.tech" data-testid="footer-mail-privacy" className="hover:text-sage-600">privacy@innercompass.tech</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border py-4 text-center text-xs text-ink-500">
        © {year} Inner Compass · {t("public.footer.rights")}
      </div>
    </footer>
  );
}
